/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React from "react";
import { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import { Box, Button, Label } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { AuditInlineDatePicker } from "~/components/Audit/AuditInlineDatePicker";
import { Fieldset } from "~/components/Forms";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { useAPIData } from "~/hooks/useAPIData";
import { CustomerSearch } from "~/models/customerSearch";
import { isValidEmail, useQueryState } from "~/tools";

export type AuditFiltersProp = {
  customerId?: string;
  startTime?: string;
  endTime?: string;
  startDate?: string;
  endDate?: string;
  operation?: string;
};

function SearchNavbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [startDate, setDateStart] = React.useState<Dayjs | null>(null);
  const [endDate, setDateEnd] = React.useState<Dayjs | null>(null);
  const [isDateStartEdit, setIsStartDateEdit] = React.useState(false);
  const [isDateEndEdit, setIsEndDateEdit] = React.useState(false);

  const [queryState, setQueryState] = useQueryState();
  const methods = useForm();

  const toggleFilters = (openFilters: boolean) => {
    setIsOpen(openFilters);
  };

  const [customers] = useAPIData<CustomerSearch[]>(
    "/search/customers?with_removed=true",
    (data) => data.searchResults,
    []
  );

  const customersOpts =
    customers.map((item: CustomerSearch) => ({
      label: item.name,
      value: item.id,
    })) || [];

  const [apiOperations] = useAPIData<string[]>(
    "/ui-data",
    (resp) => resp.apiOperations,
    []
  );

  const onSubmit = async (values: any) => {
    setQueryState("filters")({
      ...values,
      startDate: startDate?.format("YYYY-MM-DD"),
      endDate: endDate?.format("YYYY-MM-DD"),
    });
    setIsOpen(false);
  };

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <FlexContainer direction="column" align="flex-start">
        <Button
          severity="low"
          icon="filter"
          css={{ marginLeft: "1rem" }}
          onClick={() => toggleFilters(true)}
        >
          Filters
        </Button>

        <SidebarForm
          open={isOpen}
          setOpen={setIsOpen}
          title={"Filter Results"}
          formMethods={methods}
          submit={onSubmit}
        >
          <SidebarDropdown
            name="customerId"
            label="Customer"
            options={customersOpts}
            defaultValue={queryState.customerId?.toString()}
          />

          <SidebarDropdown
            name="operation"
            label="Operation"
            options={apiOperations}
            defaultValue={queryState.operation?.toString()}
          />
          <SidebarTextField
            name="identity"
            label="Identity"
            tooltip="Email address of user performing the action."
            defaultValue={queryState.identity?.toString()}
            registerOptions={{ validate: isValidEmail }}
          />

          <Fieldset>
            <FlexContainer>
              <Label name="Start Date: " required />
              <AuditInlineDatePicker
                date={startDate}
                setDate={setDateStart}
                isEditMode={isDateStartEdit}
                setIsEditMode={setIsStartDateEdit}
                left
              />
            </FlexContainer>
          </Fieldset>

          <Fieldset>
            <FlexContainer>
              <Label name="End Date: " required />
              <AuditInlineDatePicker
                date={endDate}
                setDate={setDateEnd}
                isEditMode={isDateEndEdit}
                setIsEditMode={setIsEndDateEdit}
                left
              />
            </FlexContainer>
          </Fieldset>

          <SidebarFormButtons
            submitText="Apply filters"
            submitEnabled={!!(startDate && endDate)}
            onCancel={() => {
              setIsOpen(false);
            }}
          />
        </SidebarForm>
      </FlexContainer>
    </Box>
  );
}

export { SearchNavbar };
